import { ArrowRight2, Messages1 } from 'iconsax-react';
import { sort, take } from 'rambda';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useTypedParams, useTypedState } from 'react-router-typesafe-routes';
import Alert from '@/assets/images/icons/toast/alert.svg?react';
import AppCell from '@/components/common/app-cell';
import TruncateCheck from '@/components/common/truncate-check';
import PageLayout from '@/components/layouts/page-layout/page-layout';
import LoadingLayout from '@/components/layouts/progress-layout/loading-layout';
import CommonGroupBlock from '@/components/user/common-group-block';
import UserHeaderMenu from '@/components/user/user-header-menu';
import UserHero from '@/components/user/user-hero';
import { useBrand } from '@/hooks/pwa/use-brand';
import useCreateSession from '@/hooks/sessions/use-create-session';
import { useTagsByUserId } from '@/hooks/tags/use-tags';
import { useIsDesktop } from '@/hooks/use-user-platform';
import { useBlacklisted } from '@/hooks/users/use-blacklist';
import useFriendInfo from '@/hooks/users/use-friend-info';
import ROUTES from '@/routes';
import copyTextToClipboard from '@/utils/copy-text';

export default function UserByIdRoute() {
  const { t } = useTranslation();
  const brand = useBrand();
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const { userId: friendId } = useTypedParams(ROUTES.CONTACT.USERS.BY_ID);
  const historyState = useTypedState(ROUTES.CONTACT.USERS.BY_ID);
  const { tags } = useTagsByUserId(friendId);
  const { friendInfo, isLoading } = useFriendInfo(friendId);
  const { sessionId, mutateAsync: createSession } = useCreateSession(friendId);
  const { data: isBlacklisted } = useBlacklisted(friendId);

  async function handleGoToSession() {
    if (sessionId) {
      return navigate(
        ROUTES.MESSAGES.SESSIONS.$buildPath({
          params: {
            id: sessionId,
          },
        }),
      );
    }

    const result = await createSession(friendId);
    if (result.Data?.ID) {
      return navigate(
        ROUTES.MESSAGES.SESSIONS.$buildPath({
          params: {
            id: result.Data?.ID,
          },
        }),
      );
    }
  }

  const commonGroups = useMemo(
    () =>
      take(
        3,
        sort((a, b) => b.LastMsgTimeStamp - a.LastMsgTimeStamp, friendInfo?.CommonGroup ?? []),
      ),
    [friendInfo?.CommonGroup],
  );

  return (
    <PageLayout
      title={isLoading ? t('user.title') : friendInfo?.FriendInfo.FriendNickName}
      className='gap-0'
      headerConfig={{
        navigateBack: !isDesktop,
        headerLabel: !isLoading && friendInfo?.IsFriend ? t('user.title') : t('user.title-default'),
        actions: friendInfo?.IsFriend && (
          <UserHeaderMenu
            userId={friendId}
            navigateBack={historyState.from}
          />
        ),
      }}>
      <LoadingLayout promise={isLoading}>
        {() =>
          friendInfo && (
            <div className='flex flex-1 gap-3 flex-col desktop:mb-safe-or-6 px-5  pt-4 overflow-y-auto basis-50dvh'>
              <UserHero
                headImg={friendInfo.FriendInfo.FriendHeadImg}
                nickname={friendInfo.FriendInfo.FriendNickName}
                sign={friendInfo.UserInfo?.Sign}
              />
              <div className='flex flex-col'>
                {friendInfo.IsFriend && (
                  <>
                    <AppCell
                      label={t('user.remark.label')}
                      className='px-0'
                      content={friendInfo.FriendInfo.RemarkName}
                      onClick={() => navigate(ROUTES.CONTACT.USERS.BY_ID.$.REMARK.$path({ relative: true }))}
                      chevron
                    />

                    <AppCell
                      className='px-0'
                      label={t('user.chat-id', { brandShortName: brand.shortName })}
                      content={friendInfo.FriendInfo.VChatID || ''}
                      onClick={() =>
                        friendInfo.FriendInfo.VChatID && copyTextToClipboard(friendInfo.FriendInfo.VChatID)
                      }
                    />
                    {!isDesktop && (
                      <AppCell
                        className='px-0'
                        label={t('common.tag')}
                        content={
                          <TruncateCheck
                            suffix={(tags.length > 2 && ', …') || ''}
                            forceTruncate={tags.length > 2}>
                            {(tags.length > 2 ? take(2, tags) : tags).map((tag) => tag.TagName).join(', ')}
                          </TruncateCheck>
                        }
                        onClick={() => navigate(ROUTES.CONTACT.USERS.BY_ID.$.TAGS.$path({ relative: true }))}
                        chevron
                      />
                    )}
                  </>
                )}
              </div>
              {isBlacklisted ? (
                <div className='flex items-center gap-2 text-body1 w-full justify-center mt-10'>
                  <Alert className='size-6' /> {t('blacklist.add-success')}
                </div>
              ) : (
                <>
                  <div className='flex flex-1 flex-col gap-2'>
                    <div className='border-surface border-t text-sm mt-0 pt-2'>
                      <div className='flex items-center justify-between min-h-8'>
                        <h5 className='font-bold'>{`${t('user.same-group')} (${
                          friendInfo.CommonGroup?.length ?? 0
                        })`}</h5>

                        {(friendInfo.CommonGroup?.length ?? 0) > 3 && (
                          <button
                            type='button'
                            className='btn btn-ghost btn-sm px-0'
                            onClick={() => navigate(ROUTES.CONTACT.USERS.BY_ID.$.GROUPS.$path({ relative: true }))}>
                            {t('common.view-all')}
                            <ArrowRight2 className='size-5' />
                          </button>
                        )}
                      </div>
                    </div>

                    <div className='flex flex-1 flex-col items-stretch min-h-48'>
                      <CommonGroupBlock groups={commonGroups} />
                    </div>
                  </div>

                  <div className='mb-2 flex items-end justify-stretch'>
                    {friendInfo.IsFriend && (
                      <button
                        type='button'
                        className='btn btn-secondary h-18 w-full bg-base-100'
                        onClick={handleGoToSession}>
                        <div className='flex flex-col items-center gap-2 p-3'>
                          <Messages1 className='size-6' />

                          {t('user.chat')}
                        </div>
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          )
        }
      </LoadingLayout>
    </PageLayout>
  );
}
